import React, { useEffect, useState } from "react";
import {
  List, ListItemButton, ListItemIcon, ListItemText,
  Collapse, CircularProgress, Box, Typography, Button,
  IconButton, Tooltip,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate, useLocation } from "react-router-dom";
import { useIntl } from "react-intl";

import { TREE_ICONS, STRATEGY_CHILDREN } from "../../constants/treeConfig";
import { fetchBusinessUnits } from "../../api/companyApi";
import { COMPANY_ID } from "../../config/appConfig";

const StrategyIcon = TREE_ICONS.strategy;
const BusinessUnitIcon = TREE_ICONS.businessDomain;

export default function StrategyTree() {
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  const [open, setOpen] = useState(true);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedUnits, setExpandedUnits] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    loadUnits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const loadUnits = async () => {
    setError(null);
    try {
      const data = await fetchBusinessUnits(COMPANY_ID);
      setUnits(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load business units for strategy:", e);
      setError(e.message || "API接続エラー");
    } finally {
      setLoading(false);
    }
  };

  const toggleUnit = (unitId) => {
    setExpandedUnits((prev) => ({ ...prev, [unitId]: !prev[unitId] }));
  };

  const toggleGroup = (unitId, groupKey) => {
    const key = `${unitId}_${groupKey}`;
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const go = (path) => navigate(path);
  const isActive = (path) => location.pathname === path;

  const activeStyle = {
    backgroundColor: "rgba(21, 101, 192, 0.08)",
    color: "#1565C0",
    fontWeight: 700,
    borderLeft: "3px solid #1565C0",
  };

  /** Render a leaf node (navigable item) — uses businessDomainId for routes, or businessUnitId if child.usesUnit */
  const renderLeafNode = (child, domainId, unitId, pl) => {
    const ChildIcon = child.icon;
    const path = child.usesUnit
      ? `/strategy/${domainId}/business-units/${unitId}/${child.route}`
      : `/strategy/${domainId}/${child.route}`;
    return (
      <ListItemButton
        key={child.key}
        sx={{ pl, ...(isActive(path) ? activeStyle : {}) }}
        onClick={() => go(path)}
      >
        <ListItemIcon sx={{ minWidth: 32 }}>
          <ChildIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary={t(child.labelKey)}
          primaryTypographyProps={{ fontSize: "0.8rem" }}
        />
      </ListItemButton>
    );
  };

  /** Render a group node (expandable with children) */
  const renderGroupNode = (child, unitId, domainId) => {
    const GroupIcon = child.icon;
    const groupKey = `${unitId}_${child.key}`;
    const isExpanded = expandedGroups[groupKey] || false;

    return (
      <React.Fragment key={child.key}>
        <ListItemButton
          sx={{ pl: 5 }}
          onClick={() => toggleGroup(unitId, child.key)}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <GroupIcon fontSize="small" color="action" />
          </ListItemIcon>
          <ListItemText
            primary={t(child.labelKey)}
            primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 500 }}
          />
          {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </ListItemButton>

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List dense disablePadding>
            {child.children.map((gc) => renderLeafNode(gc, domainId, unitId, 7))}
          </List>
        </Collapse>
      </React.Fragment>
    );
  };

  /** Render strategy children for a business unit */
  const renderStrategyChildren = (unit) => {
    const domainId = unit.businessDomainId;
    const unitId = unit.businessUnitId;
    return STRATEGY_CHILDREN.map((child) => {
      if (child.group) {
        return renderGroupNode(child, unitId, domainId);
      }
      return renderLeafNode(child, domainId, unitId, 5);
    });
  };

  return (
    <List dense disablePadding>
      <ListItemButton onClick={() => setOpen(!open)} sx={{ fontWeight: 600 }}>
        <ListItemIcon sx={{ minWidth: 36 }}>
          <StrategyIcon color="primary" />
        </ListItemIcon>
        <ListItemText primary={t("tree.strategy")} primaryTypographyProps={{ fontWeight: 600 }} />
        <Tooltip title={t("businessUnit.create.title")}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              navigate("/strategy/business-units/create");
            }}
            sx={{ mr: 0.5 }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>

      <Collapse in={open} timeout="auto" unmountOnExit>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : error ? (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>{error}</Typography>
            <Button size="small" startIcon={<RefreshIcon />} onClick={loadUnits}>再読込</Button>
          </Box>
        ) : units.length === 0 ? (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              事業単位がありません
            </Typography>
            <Button size="small" startIcon={<RefreshIcon />} onClick={loadUnits}>再読込</Button>
          </Box>
        ) : (
          <List dense disablePadding>
            {units.map((unit) => {
              const detailPath = `/strategy/${unit.businessDomainId}/business-units/${unit.businessUnitId}`;
              return (
                <React.Fragment key={unit.businessUnitId}>
                  {/* 事業単位フォルダ行: クリックで展開/折りたたみ（設計ツリーと同じ） */}
                  <ListItemButton
                    sx={{ pl: 3 }}
                    onClick={() => toggleUnit(unit.businessUnitId)}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <BusinessUnitIcon fontSize="small" color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary={unit.businessUnitName}
                      primaryTypographyProps={{ fontSize: "0.875rem", noWrap: true }}
                    />
                    {expandedUnits[unit.businessUnitId]
                      ? <ExpandLess fontSize="small" />
                      : <ExpandMore fontSize="small" />}
                  </ListItemButton>

                  <Collapse in={expandedUnits[unit.businessUnitId]} timeout="auto" unmountOnExit>
                    <List dense disablePadding>
                      {/* 事業単位詳細リンク（設計ツリーの「事業ドメイン」と同じ位置） */}
                      <ListItemButton
                        sx={{ pl: 5, ...(isActive(detailPath) ? activeStyle : {}) }}
                        onClick={() => go(detailPath)}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <BusinessUnitIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={t("businessUnit.title")}
                          primaryTypographyProps={{ fontSize: "0.8rem" }}
                        />
                      </ListItemButton>

                      {renderStrategyChildren(unit)}
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Collapse>
    </List>
  );
}
